<template>
  <div class="post-detail-container">
    <LoadingSpinner v-if="loading" />
    <div v-else-if="post" class="post-detail">
      <PostCard 
        :post="post"
        :public-mode="!isAuthenticated"
        :ref="(el) => { if (el) postCard = el }"
        @tips-count-updated="updateTipsCount"
        @like-updated="updateLike"
        @post-deleted="handlePostDeleted"
        @click:like="redirectToLogin"
        @click:tips="redirectToLogin"
        @click:reply="redirectToLogin"
        @click:rate="redirectToLogin"
        @click:menu="redirectToLogin"
      />
    </div>
    <div v-else class="not-found">
      <p>Post not found or has been deleted.</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PostCard from '@/components/PostCard.vue';

export default {
  name: 'PostDetail',
  components: {
    LoadingSpinner,
    PostCard
  },
  data() {
    return {
      post: null,
      loading: true,
      postCard: null,
      preloadedTips: [],
      isAuthenticated: false
    };
  },
  computed: {
    postId() {
      return this.$route.params.id;
    }
  },
  created() {
    this.checkAuthentication();
    this.fetchPostDetail();
  },
  mounted() {
    // Only auto-show tips for authenticated users
    if (this.isAuthenticated) {
      this.$nextTick(() => {
        if (this.post && this.postCard) {
          // Trigger loading of tips automatically
          this.postCard.toggleTips();
        }
      });
    }
  },
  updated() {
    // Only auto-show tips for authenticated users
    if (this.isAuthenticated) {
      this.$nextTick(() => {
        if (this.post && this.postCard && this.preloadedTips.length > 0 && !this.post.tips.length) {
          // Trigger loading of tips automatically
          this.postCard.toggleTips();
        }
      });
    }
  },
  methods: {
    checkAuthentication() {
      const token = localStorage.getItem('access_token');
      this.isAuthenticated = !!token;
    },

    async fetchPostDetail() {
      this.loading = true;
      try {
        // Fetch post details with authentication if user is authenticated
        const headers = {};
        if (this.isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        }
        
        const response = await axios.get(`https://api.6buoys.com/api/post-detail/${this.postId}/`, {
          headers
        });

        // Format the post data similar to how it's done in HomeTimeline
        this.post = {
          ...response.data,
          showTips: this.isAuthenticated, // Only show tips for authenticated users
          tips: [],
          tips_count: response.data.tips_count || 0,
          likes_count: response.data.likes_count || 0,
          liked_by_user: response.data.liked_by_user || false,
          like_id: response.data.like_id || null,
          author: response.data.author || {
            id: null,
            username: '',
            avatar: 'default-avatar.png',
            first_name: 'Unknown',
            last_name: ''
          }
        };

        // Increment view count for the post
        this.incrementPostView(this.post.id);

        // Only preload tips for authenticated users
        if (this.isAuthenticated) {
          await this.preloadTips();
        }

      } catch (error) {
        console.error('Error fetching post details:', error);
        this.post = null;
      } finally {
        this.loading = false;
      }
    },

    async preloadTips() {
      try {
        const response = await axios.get(`https://api.6buoys.com/api/post-comments/${this.post.id}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        this.preloadedTips = response.data;
      } catch (error) {
        console.error('Error preloading tips:', error);
        this.preloadedTips = [];
      }
    },

    updateTipsCount(count) {
      if (this.post) {
        this.post.tips_count = count;
      }
    },

    updateLike(likeData) {
      if (this.post) {
        this.post.liked_by_user = likeData.liked_by_user;
        this.post.likes_count = likeData.likes_count;
        this.post.like_id = likeData.like_id;
      }
    },

    handlePostDeleted() {
      // Navigate back or to a different page when post is deleted
      this.$router.push('/');
    },

    redirectToLogin() {
      this.$router.push('/login');
    },

    async incrementPostView(postId) {
      try {
        await axios.post(`https://api.6buoys.com/api/post-view/${postId}/`, {});
      } catch (error) {
        console.error(`Error incrementing view count for post ${postId}:`, error);
      }
    }
  },
};
</script>

<style scoped>
.post-detail-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 0px 56px 0px;
  background-color: #15202b;
}

.post-detail {
  display: flex;
  flex-direction: column;
}

.tips-container {
  margin-top: -10px; /* Adjust to reduce space between post and tips */
}

.not-found {
  text-align: center;
  padding: 40px 20px;
  color: #8899a6;
  font-size: 1.2em;
}

@media screen and (max-width: 600px) {
  .post-detail-container {
    padding: 20px 4px 56px 4px;
  }
}
</style> 